<template>
  <div class= "toolbar-scroll-group ma-0 pa-0">
    <verif-dialog
        :dialog="verifDialogIsOpen"
        :item="selectedItem"
        :mode="verifDialogMode"
        :producer_id="producer_id"
        @update:dialog="verifDialogIsOpen = $event"
        @save="saveItem"
        @saveTodo="saveTodo"
        @clearKeeperInfo="clearKeeperInfo"
        ></verif-dialog>
    <header class="vm-header">
      <v-btn icon="mdi-close" variant="text" density="comfortable" @click="close" :aria-label="$t('close')" />
      <div class="vm-header__text">
        <div class="vm-header__name text-truncate">{{ producer_name }}</div>
        <div class="vm-header__sub">
          {{ completed }}/{{ total }} · {{ percentage }}% {{ $t('verif_machines.complete') }}
        </div>
      </div>
      <!-- explicit filter: to do, done, or both -->
      <v-chip-group v-model="showFilter" multiple mandatory selected-class="vm-filter--on" class="vm-filter">
        <v-chip value="pending" size="small" variant="tonal" filter prepend-icon="mdi-progress-check">
          {{ $filters.capitalize($t('verif_machines.still_to_do')) }}
        </v-chip>
        <v-chip value="done" size="small" variant="tonal" filter prepend-icon="mdi-check-circle-outline">
          {{ $filters.capitalize($t('verif_machines.complete', 2)) }}
        </v-chip>
      </v-chip-group>
      <v-progress-linear
          class="vm-header__bar"
          color="success"
          bg-color="grey-lighten-3"
          height="4"
          :model-value="percentage"
          />
    </header>
    <div class="scrollable-content">
      <div v-if="selectMultiple !== 'normal'" class="vm-banner">
        <v-icon size="small" class="mr-1">mdi-checkbox-multiple-marked-outline</v-icon>
        {{ $filters.capitalize($t('verif_machines.' + selectMultiple)) }}
      </div>
      <section v-for="(group, verifCategory) in groupedData" :key="verifCategory" class="vm-group">
        <button type="button" class="vm-group__head" @click="toggleCollapse(verifCategory)">
          <v-icon size="small" class="mr-2">{{ isCollapsed(verifCategory) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          <span class="vm-group__name">{{ verifCategory }}</span>
          <v-chip size="x-small" variant="flat" :color="groupDone(verifCategory) === group.items.length ? 'success' : 'grey-lighten-2'" class="ml-auto">
            {{ groupDone(verifCategory) }} / {{ group.items.length }}
          </v-chip>
        </button>
        <table v-show="isCollapsed(verifCategory)" class="vm-table">
          <thead v-show="!isMobile">
            <tr>
              <th>{{$t('verif_machines.title')}}</th>
              <th>{{$t('verif_machines.indications')}}</th>
              <th>{{$t('verif_machines.comment')}}</th>
              <th>{{$t('verif_machines.periodicity')}}</th>
              <th>{{$t('verif_machines.last_done')}}</th>
              <th>{{$t('intervention_reports.partner')}}</th>
              <th class="vm-table__status"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in group.items"
                :key="index"
                @click="handleRowClick(item, index, group)"
                :class="getRowClass(item, index, group)"
                >
                <td class="vm-table__title">
                  {{ localized(item, 'verification_title') }}
                  <v-tooltip location="top" :text="item.keeper_info">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" v-if="item.keeper_info" size="small" class="ml-1" icon="mdi-information-outline" color="warning" />
                    </template>
                  </v-tooltip>
                  <!-- phone: date, progress and partners under the title, one tap target per row -->
                  <div v-if="isMobile && item.last_done_date" class="vm-table__meta">
                    {{ item.last_done_date }}
                    <span v-if="item.required_count > 1">({{ item.done_count }}/{{ item.required_count }})</span>
                    · {{ formatList(item.last_done_by) }}
                  </div>
                </td>
                <td v-show="!isMobile" class="vm-table__muted">{{ localized(item, 'indications') }}</td>
                <td v-show="!isMobile" class="vm-table__muted">{{ item.comment }}</td>
                <td v-show="!isMobile" class="vm-table__muted vm-table__nowrap">{{ $t('verif_machines.every_n_months', { n: item.period_months }) }}</td>
                <td v-show="!isMobile" class="vm-table__nowrap">
                  {{ item.last_done_date }}
                  <span v-if="item.required_count > 1" class="vm-table__muted">({{ item.done_count }}/{{ item.required_count }})</span>
                </td>
                <td v-show="!isMobile" class="vm-table__nowrap">{{ formatList(item.last_done_by) }}</td>
                <td class="vm-table__status">
                  <v-icon
                      v-if="selectMultiple === 'selectionMultiple' && !isComplete(item)"
                      :icon="isSelected(index, group) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"
                      color="primary"
                      @click.stop="toggleSelect(index, group)"
                      />
                  <v-icon
                      v-else-if="isOverdue(item)"
                      icon="mdi-alert-circle"
                      color="error"
                      />
                  <v-icon
                      v-else-if="item.last_done_id && item.intervention_report_id"
                      icon="mdi-clipboard-check"
                      color="primary"
                      />
                  <v-icon
                      v-else-if="isComplete(item)"
                      icon="mdi-check-circle"
                      color="success"
                      />
                  <v-icon
                      v-else-if="item.last_done_id"
                      icon="mdi-progress-check"
                      color="grey"
                      />
                </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
    <div class="footer">
      <AnnualActions
          :verifWithoutReport='verifWithoutReport'
          :areItemsSelected='areItemsSelected'
          :producer_id='producer_id'
          :someDone='someDone'
          @addItems='addItems'
          @select_multiple='handleSelectMultiple'
          @reloadVerifDone='reloadVerifDone'
          @close="close"
          @saveTodo="saveTodo"
          />
    </div>
  </div>
</template>

<script>
  import AnnualActions from "@/components/maintenance/AnnualActions";
  import VerifDialog from '@/components/maintenance/VerifDialog';
  import { isNone } from "@/functions.js";


  export default {
    name: 'VerifList',
    components: {
      AnnualActions,
      VerifDialog,
    },
    props: {
      producer_id: {},
      producer_name: {},
      dialogMode: {},
    },

    computed: {
      items(){
        return this.$store.state.verif_machines.items
      },
      groupedData(){
        let filteredList = this.items
        if (this.showMode === 'done') {
          filteredList = this.items.filter(item => this.isComplete(item));
        } else if (this.showMode === 'pending') {
          filteredList = this.items.filter(item => !this.isComplete(item));
        }
        const grouped = filteredList.reduce((acc, item) => {
          const key = this.localized(item, 'category_name')
          if (!acc[key]) {
            acc[key] = { items: []};
          }
          acc[key].items.push(item);
          return acc;
        }, {});
        return grouped;
      },
      someDone(){
        return this.items.filter(item => item.last_done_id).length > 0;
      },
      areItemsSelected(){
        return this.selectedItems.length > 0 && !isNone(this.selectedItems);
      },
      verifWithoutReport(){
        return this.items.filter(item => isNone(item.intervention_report_id) &&
          !isNone(item.last_done_id));
      },
      completed(){
        return this.items.filter(item => this.isComplete(item)).length
      },
      total(){
        return this.items.length
      },
      percentage(){
        return Math.trunc(100*this.completed/this.total)
      },
      // the rest of the component reasons in 'all' / 'done' / 'pending'
      showMode(){
        const f = this.showFilter
        if (f.includes('done') && f.includes('pending')) return 'all'
        return f.includes('done') ? 'done' : 'pending'
      },
    },

    methods: {
      handleRowClick(item, index, group) {
        if (this.selectMultiple === 'normal') {
          this.accessItem(item);
        } else if (this.selectMultiple === 'selectionMultiple') {
          this.toggleSelect(index, group);
        } else if (this.selectMultiple === 'editionMultiple') {
          this.toggleSelect(index, group);
        }
      },
        handleSelectMultiple(selection_multiple) {
          this.selectMultiple = selection_multiple;
        },
        reloadVerifDone() {
          this.$store.dispatch("verif_machines/getItemsPerProducer", {
            producer_id: this.producer_id,
            verif_category: this.dialogMode
          });
        },
        openVerifDialog(mode, item = null) {
          this.verifDialogMode = mode;
          this.selectedItem = item ? { ...item } : null;
          this.verifDialogIsOpen = true;
        },
        closeVerifDialog() {
          this.reloadVerifDone()
          this.verifDialogIsOpen = false;
        },
        clearKeeperInfo(item) {
          this.reloadVerifDone()
          this.selectedItem.keeper_info = null
        },
        getCompleteness(title){
          const inCategoryCount = this.items.filter(item =>
            item.category_name === title || item.category_name_en === title
          ).length;

          const doneInCategoryCount = this.items.filter(item =>
            (item.category_name === title || item.category_name_en === title) && this.isComplete(item)
          ).length;

          const localPluralCase = doneInCategoryCount > 1 ? 10 : 1
          switch (this.showMode) {
            case 'all':
              return `${doneInCategoryCount} / ${inCategoryCount} ` + this.$t('verif_machines.complete', localPluralCase)
              break
            case 'done':
              return `${doneInCategoryCount} / ${inCategoryCount} ` + this.$t('verif_machines.complete', localPluralCase)
              break
            case 'pending':
              return `${inCategoryCount - doneInCategoryCount} / ${inCategoryCount} ` + this.$t('verif_machines.still_to_do')
              break
          }
        },
        // English text when the user is in English and it is filled, French otherwise
        localized(item, field) {
          if (this.$store.getters.language === 'en' && item[field + '_en']) return item[field + '_en']
          return item[field]
        },
        // a 6-month task needs 2 dones in the cycle, a 3-month one 4, an annual one 1
        isComplete(item) {
          return item.done_count >= item.required_count
        },
        // recurring task (less than a year) whose last done is older than its period:
        // a 6-month greasing done in January is red from July, even if the yearly count is reached
        isOverdue(item) {
          if (!item.last_done_date || item.period_months >= 12) return false
          const due = new Date(item.last_done_date)
          due.setMonth(due.getMonth() + item.period_months)
          return due < new Date()
        },
        accessItem(item){
          // an incomplete recurring task opens in add mode: a new done, not an edit of the previous one
          if (this.isComplete(item)) this.viewItem(item)
          else this.addItem(item)
        },
        // TODO: refacto these three if all they do is
      //          call same function with different param
      viewItem(item) {
        this.openVerifDialog('view', item);
      },
        addItem(item) {
          this.openVerifDialog('add', item);
        },
        addItems() {
          this.openVerifDialog('multiple', this.selectedItems);
        },
        saveItem(item) {
          // TODO: move this to store?
            const date = new Date(item.done_at);
          const formattedDate = date.toISOString().split("T")[0];
          let v_item = {
            comment: item.comment,
            done_at: formattedDate,
            partner_ids: item.partner_ids,
            keep_info_after_closure: item.keep_info_after_closure,
            producer_id: this.producer_id,
            intervention_report_id: item.intervention_report_id,
            closure_report_id: item.closure_report_id,
          }
          let actionToDispatch = 'addItem'
          if (item.verif_id) {
            //single verif_done
            v_item.verif_id = item.verif_id
            // single edit
            if (item.pk) {
              v_item.pk = item.pk
              actionToDispatch= 'editItem'
            }
          }
          else {
            //multiple verif_done
            v_item.verif_ids = this.selectedItems.map(p => p.pk)
            // multiple edit (only in edition mode: in selection mode a partially done
            // recurring task has a last done too, and must get a new one)
            if (this.selectMultiple === 'editionMultiple' && this.selectedItems.every(item => item.last_done_id)) {
              v_item.pks = this.selectedItems.map(item => item.last_done_id)
              actionToDispatch= 'editMultiple'
            }
          }
          this.$store.dispatch(`verif_dones/${actionToDispatch}`, v_item).then(() => {
            this.$store.dispatch('snackbar/showSuccess', this.$t('snack.update_success'))
            this.selectedItems = [];
            this.reloadVerifDone()
          }).catch((e)=> {
            this.$store.dispatch("snackbar/showError",this.$t('error.default', e))
          })
        },
        toggleSelect(index, group) {
          if ((this.selectMultiple ==='selectionMultiple') && this.isComplete(group.items[index])) return
          if ((this.selectMultiple ==='editionMultiple') && !group.items[index].last_done_date) return

          const selected = this.selectedItems.findIndex(v => v.pk === group.items[index].pk)
          if (selected < 0 ) this.selectedItems.push(group.items[index])
          else this.selectedItems.splice(selected, 1);
        },
        isSelected(index, group) {
          const selected = this.selectedItems.findIndex(v => v.pk === group.items[index].pk)
          if (selected < 0) return false
          return true
        },
        getRowClass(item, index, group) {
          if (this.isSelected(index, group)) return 'row-selected';
          if (this.isOverdue(item)) return 'row-overdue';
          if (this.isComplete(item)) return 'row-complete';
          if (item.last_done_id) return 'row-partial';
          return '';
        },
        groupDone(title) {
          return this.items.filter(item =>
            (item.category_name === title || item.category_name_en === title) && this.isComplete(item)).length
        },
        checkIsMobile() {
          this.isMobile = window.innerWidth < 900; // or use any breakpoint you consider as mobile
        },
        toggleCollapse(section) {
          const index = this.collapsedSections.indexOf(section);
          if (index === -1) {
            this.collapsedSections.push(section);
          } else {
            this.collapsedSections.splice(index, 1);
          }
        },
        isCollapsed(section) {
          return this.collapsedSections.includes(section);
        },
        formatList(list) {
          return Array.isArray(list) ? list.join("/") : "";
        },
        close() {
          this.$emit('close')
        },
        saveTodo(){
          this.$store.dispatch('todos/addItem', this.$store.getters.dataEdit('todos')).then(()=>{
            this.$store.dispatch("snackbar/showSuccess",this.$t('todos.label') + ' ' + this.$t("snack.create_success"))
          })
        },
    },
    watch: {
      groupedData(gr){
        if (Object.keys(gr).length == 1) {
          this.collapsedSections = Object.keys(gr)
        }
      },
        selectMultiple(value) {
          this.selectedItems = []
          switch (value) {
            case 'selectionMultiple':
              this.showFilter = ['pending']
              break
            case 'editionMultiple':
              this.showFilter = ['done']
              break
            case 'normal':
              this.showFilter = ['pending', 'done']
          }
        },
    },
    mounted() {
      this.checkIsMobile();
      window.addEventListener('resize', this.checkIsMobile);
      //  this.$store.dispatch('int rep / update edi', thing with producer id etc)
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.checkIsMobile);
    },
    data() {
      return {
        selectedItems: [],
        isMobile: false,
        verifDialogIsOpen: false,
        collapsedSections: [],
        selectedItem: this.$store.state.verif_machines.struct,
        verifDialogMode: 'view', // 'view' or 'add'
        selectMultiple: 'normal',
        showFilter: ['pending', 'done'],
      };
    },
  };
</script>

<style scoped>
/* fills the dialog card: header and button bar stay, only the list scrolls */
.toolbar-scroll-group {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
}

/* header */
.vm-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 12px 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
.vm-header__text {
  flex: 1 1 auto;
  min-width: 0;
}
.vm-header__name {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.2;
}
.vm-header__sub {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}
.vm-filter {
  flex-shrink: 0;
}
.vm-filter :deep(.v-slide-group__content) {
  gap: 4px;
}
.vm-filter--on {
  background: rgba(25, 118, 210, 0.14);
  color: #1565c0;
}
.vm-header__bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

/* list */
.scrollable-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 12px 16px;
  background: #f6f7f9;
}
.vm-banner {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1565c0;
  background: #e3f2fd;
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 8px;
}
.vm-group {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
}
.vm-group__head {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  font: inherit;
  background: none;
  border: 0;
  cursor: pointer;
}
.vm-group__head:hover {
  background: rgba(0, 0, 0, 0.03);
}
.vm-group__name {
  font-weight: 600;
  font-size: 0.95rem;
}

/* table */
.vm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.vm-table th {
  padding: 6px 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  text-align: left;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #fafafa;
}
.vm-table td {
  padding: 8px 10px;
  vertical-align: middle;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.vm-table tbody tr {
  cursor: pointer;
  border-left: 3px solid transparent;
}
.vm-table tbody tr:hover {
  background: rgba(25, 118, 210, 0.06);
}
.vm-table__title {
  font-weight: 500;
}
.vm-table__muted {
  color: rgba(0, 0, 0, 0.6);
}
.vm-table__meta {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  font-weight: 400;
  margin-top: 2px;
}
.vm-table__nowrap {
  white-space: nowrap;
}
.vm-table__status {
  width: 44px;
  text-align: center;
}

/* row states: a coloured edge, a faint tint */
.row-complete {
  border-left-color: #2e7d32 !important;
  background: #c8e6c9;
}
.row-complete:hover {
  background: #a5d6a7 !important;
}
.row-partial {
  border-left-color: #9e9e9e !important;
}
.row-overdue {
  border-left-color: #d32f2f !important;
  background: #fdecea;
}
.row-overdue:hover {
  background: #f9d2cf !important;
}
.row-selected {
  border-left-color: #1976d2 !important;
  background: #e3f2fd;
}

.footer {
  flex-shrink: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
}

/* phone: real viewport height, bigger tap targets */
@media (max-width: 599px) {
  .scrollable-content {
    padding: 8px;
  }
  .vm-table td {
    padding: 12px 10px;
  }
}
</style>
